import { useState } from 'react';
import { FaCheck, FaTrash, FaPlus, FaEdit, FaSave, FaTimes, FaRegCircle, FaCheckCircle } from 'react-icons/fa';
import { useBaseComponentFullscreen } from './BaseComponent';

export default function TodoComponent({ component, onUpdate }) {
  const isFullscreen = useBaseComponentFullscreen();
  const data = component.data || { tasks: [] };
  const tasks = data.tasks || [];
  const [newTask, setNewTask] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editText, setEditText] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dropPosition, setDropPosition] = useState(null); // 'above' or 'below'

  // Group tasks but preserve order for reordering
  const groupedTasks = tasks.reduce((acc, task, originalIndex) => {
    const key = task.completed_at ? 'completed' : 'active';
    acc[key].push({ ...task, originalIndex });
    return acc;
  }, { active: [], completed: [] });

  // Sort active tasks by order (assign default order based on array index if no order exists)
  groupedTasks.active.sort((a, b) => {
    // If both have order, use order
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }
    // If only one has order, prioritize the one with order
    if (a.order !== undefined && b.order === undefined) {
      return -1;
    }
    if (a.order === undefined && b.order !== undefined) {
      return 1;
    }
    // If neither has order, maintain their current array position (don't sort by date)
    return 0;
  });
  
  // Sort completed tasks by completion date (newest first)
  groupedTasks.completed.sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));

  // Add new task
  const addTask = () => {
    if (!newTask.trim()) return;
    
    // Get the current count of active tasks to set the order
    const activeTaskCount = tasks.filter(task => !task.completed_at).length;
    
    const updatedTasks = [
      ...tasks,
      {
        text: newTask.trim(),
        created_at: new Date().toISOString(),
        completed_at: null,
        order: activeTaskCount // Set order to be at the end of active tasks
      }
    ];

    handleUpdate({ tasks: updatedTasks });
    setNewTask('');
  };

  // Toggle task completion
  const toggleTask = (index) => {
    const updatedTasks = [...tasks];
    const isCompleting = !updatedTasks[index].completed_at;
    
    updatedTasks[index] = {
      ...updatedTasks[index],
      completed_at: isCompleting ? new Date().toISOString() : null,
      order: isCompleting ? undefined : undefined // Clear order when completing, will be set later if needed
    };
    
    // If we're uncompleting a task, give it a new order at the end of active tasks
    if (!isCompleting) {
      const activeTaskCount = updatedTasks.filter(task => !task.completed_at).length;
      updatedTasks[index].order = activeTaskCount - 1; // -1 because we already counted this task
    }
    
    handleUpdate({ tasks: updatedTasks });
  };

  // Delete task
  const deleteTask = (index) => {
    const updatedTasks = tasks.filter((_, i) => i !== index);
    handleUpdate({ tasks: updatedTasks });
  };

  // Start editing a task
  const startEditing = (index, e) => {
    e.stopPropagation();
    setEditingIndex(index);
    setEditText(tasks[index].text);
  };

  // Save edited task
  const saveEdit = (index, e) => {
    e.stopPropagation();
    if (!editText.trim()) return;
    
    const updatedTasks = [...tasks];
    updatedTasks[index] = {
      ...updatedTasks[index],
      text: editText.trim()
    };

    handleUpdate({ tasks: updatedTasks });
    setEditingIndex(null);
  };

  // Cancel editing
  const cancelEdit = (e) => {
    e.stopPropagation();
    setEditingIndex(null);
  };

  // Drag and drop handlers - simplified to use display indices
  const handleDragStart = (e, displayIndex) => {
    e.stopPropagation();
    setDraggedIndex(displayIndex);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, displayIndex) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(displayIndex);
    
    // Calculate if cursor is in top or bottom half of the element
    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const position = e.clientY < midpoint ? 'above' : 'below';
    setDropPosition(position);
  };

  const handleDragLeave = (e) => {
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
      setDropPosition(null);
    }
  };

  const handleDrop = (e, dropDisplayIndex) => {
    e.preventDefault();
    e.stopPropagation();
    const currentDropPosition = dropPosition;
    setDragOverIndex(null);
    setDropPosition(null);
    
    if (draggedIndex === null) {
      setDraggedIndex(null);
      return;
    }
    
    // Work with active tasks only
    const activeTasks = [...groupedTasks.active];
    const draggedTask = activeTasks[draggedIndex];
    
    // Remove the dragged task first
    activeTasks.splice(draggedIndex, 1);
    
    // Calculate where to insert based on drop position
    let insertIndex = dropDisplayIndex;
    
    if (currentDropPosition === 'below') {
      // If dropping below, we want to insert after the target
      if (draggedIndex < dropDisplayIndex) {
        // Moving down: we already removed an item before, so position is already correct
        insertIndex = dropDisplayIndex;
      } else {
        // Moving up: insert after the target (add 1)
        insertIndex = dropDisplayIndex + 1;
      }
    } else {
      // If dropping above
      if (draggedIndex < dropDisplayIndex) {
        // Moving down: we removed an item before, so subtract 1
        insertIndex = dropDisplayIndex - 1;
      } else {
        // Moving up: insert before the target (position is correct)
        insertIndex = dropDisplayIndex;
      }
    }
    
    // Ensure insertIndex is within bounds
    insertIndex = Math.max(0, Math.min(insertIndex, activeTasks.length));
    
    // Insert at the calculated position
    activeTasks.splice(insertIndex, 0, draggedTask);
    
    console.log(`Moved task from position ${draggedIndex} to final position ${insertIndex} (${currentDropPosition})`);
    
    // Rebuild the full tasks array with new order
    const completedTasks = tasks.filter(task => task.completed_at);
    const reorderedActiveTasks = activeTasks.map((task, index) => ({
      ...task,
      order: index
    }));
    
    const updatedTasks = [...reorderedActiveTasks, ...completedTasks];

    handleUpdate({ tasks: updatedTasks });
    setDraggedIndex(null);
  };

  const handleDragEnd = (e) => {
    e.stopPropagation();
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDropPosition(null);
  };

  // Handle update and send to parent
  const handleUpdate = (newData) => {
    onUpdate({
      ...component,
      data: {
        ...data,
        ...newData
      }
    });
  };

  return (
    <div className="max-w-2xl mx-auto h-full flex flex-col">
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          className="flex-1 p-2 border border-[var(--border-color)] rounded focus:outline-none focus:ring-1 focus:ring-[var(--primary)] bg-[var(--bg-primary)]"
          value={newTask}
          onChange={(e) => setNewTask(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && addTask()}
          placeholder="Add a new task"
          maxLength={255}
        />
        <button
          onClick={addTask}
          className="p-2 bg-[var(--primary)] text-white rounded hover:opacity-90"
        >
          <FaPlus />
        </button>
      </div>
      
      <div className={`flex-1 min-h-0 overflow-y-auto`}>
        {/* Active Tasks */}
        <ul className="space-y-2">
          {groupedTasks.active.map((task, displayIndex) => (
            <TaskItem
              key={task.originalIndex}
              task={task}
              index={task.originalIndex}
              displayIndex={displayIndex}
              editingIndex={editingIndex}
              editText={editText}
              setEditText={setEditText}
              toggleTask={toggleTask}
              startEditing={startEditing}
              saveEdit={saveEdit}
              cancelEdit={cancelEdit}
              deleteTask={deleteTask}
              handleDragStart={handleDragStart}
              handleDragOver={handleDragOver}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              handleDragEnd={handleDragEnd}
              isDragged={draggedIndex === displayIndex}
              isDragOver={dragOverIndex === displayIndex}
              dropPosition={dragOverIndex === displayIndex ? dropPosition : null}
              canDrag={true}
            />
          ))}
        </ul>

        {/* Completed Tasks (only show if there are any) */}
        {groupedTasks.completed.length > 0 && (
          <>
            <div className="mt-6 mb-2 text-sm font-medium text-[var(--text-secondary)]">
              Completed ({groupedTasks.completed.length})
            </div>
            <ul className="space-y-2">
              {groupedTasks.completed.map((task, index) => (
                <TaskItem
                  key={task.originalIndex}
                  task={task}
                  index={task.originalIndex}
                  editingIndex={editingIndex}
                  editText={editText}
                  setEditText={setEditText}
                  toggleTask={toggleTask}
                  startEditing={startEditing}
                  saveEdit={saveEdit}
                  cancelEdit={cancelEdit}
                  deleteTask={deleteTask}
                  handleDragStart={handleDragStart}
                  handleDragOver={handleDragOver}
                  handleDragLeave={handleDragLeave}
                  handleDrop={handleDrop}
                  handleDragEnd={handleDragEnd}
                  isDragged={draggedIndex === task.originalIndex}
                  isDragOver={dragOverIndex === task.originalIndex}
                  canDrag={false}
                />
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

// Extracted TaskItem component for better readability
function TaskItem({ 
  task, 
  index, 
  displayIndex,
  editingIndex, 
  editText, 
  setEditText, 
  toggleTask, 
  startEditing, 
  saveEdit, 
  cancelEdit, 
  deleteTask, 
  handleDragStart, 
  handleDragOver, 
  handleDragLeave, 
  handleDrop, 
  handleDragEnd, 
  isDragged, 
  isDragOver, 
  dropPosition,
  canDrag 
}) {
  return (
    <li 
      className={`p-3 rounded flex items-start gap-3 relative group transition-all duration-200 ${
        task.completed_at 
          ? 'bg-[var(--bg-secondary)] opacity-75' 
          : 'bg-[var(--bg-primary)] hover:bg-[var(--bg-hover)]'
      } ${
        isDragged ? 'opacity-50 scale-95' : ''
      } ${
        canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
      }`}
      onClick={(e) => {
        // Don't toggle task if we're dragging or clicking on action buttons
        if (!isDragged && !e.target.closest('button') && !e.target.closest('input')) {
          toggleTask(index);
        }
      }}
      draggable={canDrag}
      onDragStart={canDrag ? (e) => {
        e.stopPropagation();
        handleDragStart(e, displayIndex);
      } : undefined}
      onDragOver={canDrag ? (e) => {
        e.stopPropagation();
        handleDragOver(e, displayIndex);
      } : undefined}
      onDragLeave={canDrag ? (e) => {
        e.stopPropagation();
        handleDragLeave(e);
      } : undefined}
      onDrop={canDrag ? (e) => {
        e.stopPropagation();
        handleDrop(e, displayIndex);
      } : undefined}
      onDragEnd={canDrag ? (e) => {
        e.stopPropagation();
        handleDragEnd(e);
      } : undefined}
    >
      {/* Drop indicator line */}
      {isDragOver && dropPosition === 'above' && (
        <div className="absolute left-0 right-0 top-0 h-0.5 bg-[var(--primary)] -mt-1 z-10" />
      )}
      {isDragOver && dropPosition === 'below' && (
        <div className="absolute left-0 right-0 bottom-0 h-0.5 bg-[var(--primary)] -mb-1 z-10" />
      )}
      
      {/* Completion Checkbox */}
      <div className="p-1 mt-1 rounded-full flex-shrink-0">
        {task.completed_at ? (
          <FaCheckCircle className="text-[var(--primary)] text-lg" />
        ) : (
          <FaRegCircle className="text-[var(--secondary)] group-hover:text-[var(--primary)] text-lg transition-colors" />
        )}
      </div>
      
      {/* Task Content */}
      <div className="flex-1">
        {editingIndex === index ? (
          <input
            type="text"
            className="w-full p-2 border border-[var(--border-color)] rounded bg-[var(--bg-secondary)]"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && saveEdit(index, e)}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        ) : (
          <p className={`${
            task.completed_at ? 'line-through text-[var(--text-secondary)]' : 'text-[var(--text-primary)]'
          }`}>
            {task.text}
          </p>
        )}
        <div className="text-xs text-[var(--text-secondary)] mt-1">
          {task.created_at && (
            <span>Added: {new Date(task.created_at).toLocaleString()}</span>
          )}
          {task.completed_at && (
            <span> • Completed: {new Date(task.completed_at).toLocaleString()}</span>
          )}
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {editingIndex === index ? (
          <>
            <button
              onClick={(e) => saveEdit(index, e)}
              className="p-1 text-[var(--primary)] hover:text-[var(--primary)] hover:opacity-80 transition-opacity"
            >
              <FaSave />
            </button>
            <button
              onClick={(e) => cancelEdit(e)}
              className="p-1 text-[var(--secondary)] hover:text-danger transition-colors"
            >
              <FaTimes />
            </button>
          </>
        ) : (
          <>
            <button
              onClick={(e) => startEditing(index, e)}
              className="p-1 text-[var(--secondary)] hover:text-[var(--primary)] transition-colors"
              disabled={task.completed_at}
            >
              <FaEdit />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                deleteTask(index);
              }}
              className="p-1 text-[var(--secondary)] hover:text-danger transition-colors"
            >
              <FaTrash />
            </button>
          </>
        )}
      </div>
    </li>
  );
}